
import React, { Fragment } from "react"

export var projectInfoList = [
    {
        title: "Supporting Informal Healthcare Teams at Home using Sensor Data",
        description: `Being seriously injured with neurological impairments, such as spinal cord injuries, might require a team of people (the care team), including family, paid assistants, and clinicians, to assist with everyday activities. We seek to design sensor-based mobile platforms and tools for supporting the collaboration among care team members to achieve desired health outcomes.`,
        status: `on-going, published`,
        details_link: `/care-team-collaboration/`,
        project_tag: `sci`,
        image_info: {
            link: `/images/project/hush-naidoo-pA0uoltkwao-unsplash_390x200.jpg`, //"/images/project/livingroom_by_tylerkaraszewski@Flickr.jpg",
            original_link: `https://unsplash.com/photos/pA0uoltkwao`, //"https://www.flickr.com/photos/tylerkaraszewski/11358754553",
            caption: ``,
            license_info: {
                link: "https://unsplash.com/photos/pA0uoltkwao",
                // <div>Photo by <a href="https://unsplash.com/@hush52" target="_blank" rel="noopener noreferrer">Hush Naidoo</a> on Unsplash</div>
                text: `Photo by Hush Naidoo on Unsplash`
            }
        },
        role_list: ["Project Leader", "Researcher", "Designer", "Developer"],
        collaborator_list: ["Dr. Mark S. Ackerman", "Dr. Ayşe Büyüktür", "Dr. Michelle Meade", "Dr. Mark W. Newman",],
        method_list: ["Contextual Inquiry", "Co-design", "Focus Group", "Prototyping", "Usability Testing", "Questionnaire", "Persona", "Scenario", "Within-Subject Experiment", "Thematic Analysis", "Affinity Wall", "Card Sorting"],
        skill_list: ["Javascript", "CSS", "HTML", "React", "Node.js", "NoSQL", "R", "Qualtrics"],
        topic_list: ["sensor", "data", "privacy", "health", "care", "chronic condition", "patient", "caregiver", "clinician"],
        introduction: <Fragment>
            <p><strong>Background:</strong> This project is a major part of my dissertation research. Through this project I have gained a deeper understanding of how neurological impairements affect people and their families' life and I have found opportunities to use my skills to support them.</p>
            <p>I joined the project initially to assist a senior Ph.D. student, Ayşe Büyüktür, with a qualitative study and data analysis, under the mentorship of two faculty advisors. Since then, I have taken the lead to focus particularly with respect to using my background in computer science to explore interactive system design.</p>
        </Fragment>,
        summary: <Fragment>
            <p><strong>Motivation:</strong> Teenagers with a chronic health conditions, such as spinal cord injuries deal with enormous challenges, including managing ever-changing health status, working with their care-teams in their everyday lives, maintaining their self-image, and coping with social expectation for them to be independent in the process of transitioning to adulthood.
            </p>

            <div className="hero">
                <div className="hero-body">
                    <div className="is-size-4 is-size-5-mobile has-text-left has-text-weight-semibold">
                        "When you are teenage, you start becoming self-conscious about everything."
                    </div>
                    <br />
                    <div className="is-size-5 is-size-6-mobile is-pulled-right">
                        ~ Patient/Co-design Partner
                    </div>
                </div>
            </div>

            <p><strong>Problem Solving:</strong> To provide important observations of the care experience, my team and I conducted interviews with 27 stakeholders to understand how sensor-based technology can support care-team collaboration. As stakeholders might be new to sensors and data, I further conducted co-design activities with 1 patient so that insights could be drawn from sharing our knowledge and experience in continuous engagement.</p>

            <p>I developed a system, Data Checkers, that enables users to visually compose sharing settings for fine-grained control and have evaluated it with 15 patients and caregivers through a comparison study.</p>

            <p className="is-hidden">I am currently developing and evaluating interactive systems to provide concrete examples that embody the lessons learned from the study.</p>

            <div>
                <div className="is-size-5  is-size-5-mobile is-hidden">Process Overview</div>
                <ul className="steps">
                    <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }} >
                        <span className="steps-marker"></span>
                        <div className="steps-content is-divider-content">
                            <p className="is-size-5">Interview & Focus Group</p>
                            <p>27 stakeholders</p>
                        </div>
                    </li>
                    <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }}>
                        <span className="steps-marker"></span>
                        <div className="steps-content is-divider-content">
                            <p className="is-size-5">Co-design</p>
                            <p>1 patient</p>
                        </div>
                    </li>
                    <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none", display: "none" }}>
                        <span className="steps-marker"></span>
                        <div className="steps-content is-divider-content">
                            <p className="is-size-5">Prototype</p>
                        </div>
                    </li>
                    <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none", display: "none" }}>
                        <span className="steps-marker"></span>
                        <div className="steps-content is-divider-content">
                            <p className="is-size-5">Usability Testing</p>
                            <p>27 participants</p>
                        </div>
                    </li>
                    <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none", display: "none" }}>
                        <span className="steps-marker"></span>
                        <div className="steps-content is-divider-content">
                            <p className="is-size-5">Re-design</p>
                        </div>
                    </li>
                    <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }}>
                        <span className="steps-marker"></span>
                        <div className="steps-content is-divider-content">
                            <p className="is-size-5">Protootype & Evaluations</p>
                            <p>15 participants</p>
                        </div>
                    </li>
                    <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }}>
                        <span className="steps-marker"></span>
                        <div className="steps-content is-divider-content">
                            <p className="is-size-5">Card Sorting</p>
                            <p>24 participants</p>
                        </div>
                    </li>
                    <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }}>
                        <span className="steps-marker"></span>
                        <div className="steps-content is-divider-content">
                            <p className="is-size-5">Interviews</p>
                            <p>30 participants</p>
                        </div>
                    </li>
                    <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }}>
                        <span className="steps-marker"></span>
                    </li>
                </ul>
                <figcaption style={{ textAlign: "center" }}>
                </figcaption>
            </div>

            <div className="hero is-hidden">
                <div className="hero-body">
                    <div className="is-size-4 is-size-5-mobile has-text-left has-text-weight-semibold">
                        "[The goal of tracking and sharing these data is to achieve] healthiness with a purpose, instead of healthiness just to strengthen your life..."
                    </div>
                    <br />
                    <div className="is-size-5 is-size-6-mobile is-pulled-right">
                        ~ Patient/Co-design Partner
                    </div>
                </div>
            </div><br />

            <div>
                <p><strong>Outcomes:</strong></p>
                <p>Findings:</p>
                <ul>
                    <li><strong>Independence is achieved collaboratively</strong>: a patient's independence is achieved through collaboration since the distribution of responsibilities directly affects the quality of independence.</li>
                    <li><strong>Responsibility is continuously redistributed</strong>: the distribution of responsibilities changes based on the patient's health and the stability of different care routines.</li>
                </ul>
                <p>Major design suggestions:</p>
                <ul>
                    <li><strong>Support fine-grained control to enable a sense of independence</strong>: sensor-based technology should allow data sharing to be continuously negotiated with fine-grained control among a patient, family, paid caregivers, and clinicians to accommodate the patient's desired level of independence.</li>
                    <li><strong>Tailor system proactiveness to provide an appropriate level of engagemnet</strong>: sensor-based technology should tailor user engagement with different levels of proactiveness to align with the stability of care routines.</li>
                </ul>

                <p>I am still continuing the design and evaluation of Data Checkers. Initial analysis shows that participants can use the visual composition environment provided by Data Checkers to create and adjust sharing settings efficiently and flexibly. I have recently conducted two additional studies, and I am currently writing reports to summarize my findings on how to further simplify the process of creating sharing settings and using additional support to manage sharing through team collaboration.</p>

                <div className="hero">
                    <div className="hero-body">
                        <div className="is-size-4 is-size-5-mobile has-text-left has-text-weight-semibold">
                            "[Sharing data that is] sensitive enough but not so sensitive!"
                        </div>
                        <br />
                        <div className="is-size-5 is-size-6-mobile is-pulled-right">
                            ~ Patient/Co-design Partner
                        </div>
                    </div>
                </div><br />

                <p>The outcomes of this project include several scholarly publications (<a href="#publications">link</a>) and a web-based prototype that allows patients to create sharing settings through a grid-based user interface. </p>
                <br />
                <p><strong>Summary</strong>:</p>
                <nav className="level">
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Needs Finding</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">28 stakeholders</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Interactive Systems</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">2 web apps</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Usability Comparison</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">15 participants</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Scholarly Publications</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">6 articles</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Posters & Showcases</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">3 sessions</p>
                        </div>
                    </div>

                </nav>
            </div>
        </Fragment>
        // Care activities are being routinized continuously to provide stability of care. Sensor-based technology should tailor user engagement with different levels of proactiveness accordingly to provide the necessary support.
        // Patients desire to gain independence progressively with the support from an ever-changing care-team (e.g., family, paid caregivers, and clinicians). Sensor-based technology should allow data sharing to be continuously negotiated among care-team members to accommodate such a need.
    },
    {
        title: "Covid19Vaxplorer: Vaccine Allocation Strategy Comparison",
        description: `Being seriously injured with neurological impairments, such as spinal cord injuries, might require a team of people (the care team), including family, paid assistants, and clinicians, to assist with everyday activities. We seek to design sensor-based mobile platforms and tools for supporting the collaboration among care team members to achieve desired health outcomes.`,
        status: `on-going`,
        details_link: `/vaccine-allocation-strategy-comparison/`,
        project_tag: `cva`,
        image_info: {
            link: `/images/project/jason-coudriet-eQux_nmDew0-unsplash_390x200.jpg`, //"/images/project/livingroom_by_tylerkaraszewski@Flickr.jpg",
            original_link: `https://unsplash.com/photos/eQux_nmDew0`,
            caption: ``,
            license_info: {
                link: "https://unsplash.com/photos/eQux_nmDew0",
                text: `Photo by Jason Coudriet on Unsplash`
            }
        },
        role_list: ["Designer", "Developer"],
        collaborator_list: ["Dr. Laura Matrajt", "Dr. Dobromir Dimitrov", "Dr. Imelda Trejo Lorenzo",],
        method_list: ["Card Sorting", "Scenario", "Paper Prototyping"],
        skill_list: ["Javascript", "CSS", "HTML", "Vue.js", "Python", "Flask"],
        topic_list: ["health", "health official", "vaccine", "allocation", "strategy"],
        introduction: <Fragment>
            <p>
                <strong>Background:</strong>
                This is my second project aiming at making an impact on a larger, potentially global scale. This is also my first project that directly tackles issues surrounding the ongoing pandemic. Through this project, I learned how to communicate with applied mathematicians to understand the way they think about modeling and advocate for user experience as a software developer.</p>
            <p>
                After collaborating with Dr. Rafael Meza from the School of Public Health for several years on designing and building a lung cancer screening decision aid (shouldiscreen.com), he connected me with his colleague, Dr. Laura Matrajt from the Fred Hutchinson Cancer Center, who was interested in creating a tool to simulate vaccine allocation strategy for a global pandemic. I worked as both a user experience advocate and a software developer to design and implement this tool.
            </p>
        </Fragment>,
        summary: <Fragment>
            <p><strong>Motivation:</strong>
                Strategizing vaccine allocation for a pandemic is not an easy task. Health officials need to consider a complex set of factors, including the population composition, the level of social distancing on various occasions, current infection, vaccine availability, and how the population is vaccinated. With the outbreak of the pandemic, it is challenging to make vaccine allocation decisions when the stake is high.
            </p>

            <p><strong>Problem Solving:</strong>
                I worked with applied mathematicians to understand the various factors that can be modeled to predict the status of an ongoing infectious disease, including the number of cases. Given a large number of parameters (i.e., 191), I applied card sorting within our team to build consensus on a usable information architecture for organizing these parameters. I used paper prototyping as a medium to iterate our design and implement the proposed tool.</p>

            <p>I developed a system, Covid19Vaxplorer, that enables users to specify parameters in a step-by-step process to illustrate one or multipe vaccine allocation strategies and allow side-by-side comparison of the simulation outcomes using visualization.</p>

            <div>
                <div className="is-size-5  is-size-5-mobile is-hidden">Process Overview</div>
                <ul className="steps">
                    <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }} >
                        <span className="steps-marker"></span>
                        <div className="steps-content is-divider-content">
                            <p className="is-size-5">Requirement Gathering</p>
                            <p>2 experts</p>
                        </div>
                    </li>
                    <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }}>
                        <span className="steps-marker"></span>
                        <div className="steps-content is-divider-content">
                            <p className="is-size-5">Card Sorting</p>
                        </div>
                    </li>
                    <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }}>
                        <span className="steps-marker"></span>
                        <div className="steps-content is-divider-content">
                            <p className="is-size-5">Paper Prototyping</p>
                        </div>
                    </li>
                    <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }}>
                        <span className="steps-marker"></span>
                    </li>
                </ul>
                <figcaption style={{ textAlign: "center" }}>
                </figcaption>
            </div>
            <div>
                <p><strong>Outcomes:</strong></p>
                <p>The outcomes of this project include one manuscript (<a href="#publications">link</a>) in submission and a web-based tool that allows health officials to simulate different vaccine allocation strategies. </p>
                <p>We are currently testing the tool with colleagues to get feedback.</p>
                <br />
                <p><strong>Summary</strong>:</p>
                <nav className="level">
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Interactive Systems</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">1 web app</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Scholarly Publications</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">1 article (in submission)</p>
                        </div>
                    </div>
                </nav>
            </div>
        </Fragment>
    },
    {
        title: `Should I Screen? Lung Cancer Screening Decision Aid`,
        description: <Fragment>Lung cancer has been the leading cause of cancer-related mortality. Screening for lung cancer has only been recommended recently and the benefits, harms, and eligibility criteria, are not well understood by the general public. We designed a multilingual (English, Spanish, and Chinese) <a href="https://shouldiscreen.com/" target="_blank" rel="noopener noreferrer">online decision aid</a> for people and doctors to consult with during the shared decision-making process for lung cancer screening.</Fragment>,
        status: `on-going, published`,
        details_link: `/lung-cancer-screening/`,
        project_tag: `sis`,
        image_info: {
            link: "/images/project/Lungs-banner_390x200.jpg",
            original_link: ``, //`https://unsplash.com/photos/q3FihXQ-13M`,
            caption: `mcmurryjulie (pixabay.com)`, // `Photo by Cristina Gottardi on Unsplash`,
            license_info: {
                link: ``,
                text: ``
            }
        },
        role_list: ["Project Co-leader", "Researcher", "Developer", "Volunteer Organizer", "Supervisor"],
        collaborator_list: ["Dr. Yan Kwan Lau", "Dr. Mark S. Ackerman", "Dr. Rafael Meza"],
        method_list: ["Participatory Design", "Focus Group", "Prototyping", "Thematic Analysis", "Affinity Wall", "A/B Testing"],
        skill_list: ["Javascript", "CSS", "HTML", "React", "Node.js", "PHP", "SQL", "Google Analytics"],
        topic_list: ["health", "cancer", "decision aid", "literacy", "clinician", "patient"],
        // because 1) relatively few people know about it, and 2) its screening criteria is slightly more comprehensive than other tests, which includes both age and smoking history.
        // for traditionally under-screened population. 
        //  to envision a more inclusive decision aid that was more attentive to people's background and experience
        introduction: <Fragment>
            <p><strong>Background:</strong> This project is my first inter-department collaboration between the School of Information and the School of Public Health. The work allowed me to hone my research skills by applying different research methods to enhance an existing product for a population with limited resources and literacy.</p>
            <p>My friend and colleague, Lisa Lau, approached me to see whether I could bring my expertise in user-centered design to improve a web-based decision aid that her team had developed. Under the mentorship of two faculty from both schools, I orchestrated the migration of the site to a university-owned web hosting to allow for full control and redesigned the architecture to enable the parallel development of feature and content. I led a participatory design study and triangulated data with other on-going studies to inform the design of this decision aid.</p>
        </Fragment>,

        summary: <Fragment>
            <p><strong>Motivation:</strong> Lung cancer is one of the leading causes of death. A screening technique using low-dose computed tomography (CT) was recently tested and recommended. However, the screening rate is low, particularly for communities with lower socioeconomic status and limited access to resources.</p>

            <p><strong>Problem Solving:</strong> I explored ways of designing a web-based decision aid to be more inclusive by working with Latinx and African American participants (N=38) from a low-resource community in Detroit, Michigan, USA.</p>
            <p>I collaborated with public health researchers to conduct participatory design workshops with patients to envision a design that was attentive to patients' background and experience. To obtain health professionals' perspectives, I presented the findings to physicians who specialize in lung cancer screening. I then integrated their comments with evaluative feedback from focus groups to provide holistic design suggestions.</p>

            <ul className="steps">
                <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }} >
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Participatory Design</p>
                        <p>17 African Americans and Latinx</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Focus Group</p>
                        <p>21 African Americans</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Initial Analysis</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Expert Feedback</p>
                        <p>2 Physicians and 2 Community Partners</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Design & Development (A/B Testing)</p>
                    </div>
                </li>
                <li className="steps-segment  is-active" style={{ marginTop: "4px", listStyle: "none" }}>
                    <span className="steps-marker"></span>
                </li>
            </ul>
            <br />

            <div>
                <p><strong>Outcomes:</strong> Through performing thematic analysis with my colleagues, I identified three design suggestions to augment existing content-based guidelines</p>
                <ul>
                    <li><strong>Incorporate everyday language</strong>: Patients need everyday language to convey a sense of relevance to sustain their engagement.</li>
                    <li><strong>Highlight the process nature of screening</strong>: A decision aid needs to bridge the gap between patients' understanding of the screening timeline and that of physicians.</li>
                    <li><strong>Introduce social learning environment</strong>: A decision aid could consider providing a non-judgemental social learning environment with peers, which was found beneficial by patients.</li>
                </ul>
                <p>The outcomes of this project include a scholarly publication (<a href="#publications">link</a>), and a <span className="has-text-weight-semibold">multilingual web-based decision aid</span> was created for English and Spanish speaking individuals around the world with <span className="has-text-weight-semibold">7 K weekly visits</span>. In addition to maintaining and supporting the continued refinement (e.g., A/B Testing), I am currently leading the effort to incorporate these findings into the next version through mentoring undergrad research and organizing volunteers to integrate a third language, Traditional/Simplified Chinese (currently in preview).</p>

                <p><strong>Summary</strong>:</p>
                <nav className="level">
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Needs Finding</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">38 stakeholders</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Interactive System</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">1 web app</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Expert Feedback</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">4 experts</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Scholarly Publication</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">2 articles</p>
                        </div>
                    </div>
                </nav>
            </div>
        </Fragment>
    },
    {
        title: `Facilitate Knowledge Sharing among BIPOC Public Interest Technology Entrepreneurs`,
        description: <Fragment>The Public Interest Technology Knowledge Network (PIT-KN) is a new platform that elevates the lived experiences of Black, Indigenous, and People of Color (BIPOC) entrepreneurs in the field of Public Interest Technology.</Fragment>,
        status: `on-going`,
        details_link: `/public-interest-technology-network/`,
        project_tag: `pitkn`,
        image_info: {
            link: "/images/project/pitkn_390x200.jpeg", // /images/project/Lungs-banner_390x200.jpg
            original_link: `https://unsplash.com/photos/IgUR1iX0mqM`, //`https://unsplash.com/photos/q3FihXQ-13M`,
            caption: `Photo by Lagos Techie on Unsplash`, // mcmurryjulie (pixabay.com) `Photo by Cristina Gottardi on Unsplash`,
            license_info: {
                link: ``,
                text: ``
            }
        },
        role_list: ["Developer", "Supervisor"],
        collaborator_list: ["Dr. Tayo Fabusuyi", "Raymar Hampshire", "Anuya Karnik", "Yun Lee", "Jessica Taketa", "Antonia Sweet", "Jihee Yoon"],
        method_list: ["Prototyping", "Thematic Analysis", "Affinity Wall"],
        skill_list: ["Javascript", "CSS", "Google Cloud Platform", "HTML", "React", "NextJS", "NoSQL"],
        topic_list: ["public interest technology", "entrepreneur", "BIPOC", "lived experience", "knowledge", "sharing"],
        // because 1) relatively few people know about it, and 2) its screening criteria is slightly more comprehensive than other tests, which includes both age and smoking history.
        // for traditionally under-screened population. 
        //  to envision a more inclusive decision aid that was more attentive to people's background and experience
        introduction: <Fragment>
            <p><strong>Background:</strong> This project is a multi-unit collaboration between the School of Information, the Ford School of Public Policy, the College of Engineering, and external advisors working in the area of public policy. This project allowed me to revisit a past project of mine on augmenting interaction in a knowledge-based online community. Moreover, it enabled me to apply my research and software development skills, and at the same time to assume more mentoring and supervising responsibilities to lead a team effort in research and development.</p>
            <p>
                I initially ran into this summer opportunity and joined the team as a generalist to provide guidance and support on research, design, and development of an application concept at its infant stage. As the project progressed, I took up more system design and development work to create the first system prototype of an online community. I am currently serving as a mentor and supervisor to lead a team consisting of two designers, and a developer/data scientist to develop the next version of this exciting platform.
            </p>
        </Fragment>,

        summary: <Fragment>
            <p><strong>Motivation:</strong> The lived experience of Black, Indigenous, and People of Color (BIPOC) entrepreneurs in the field of Public Interest Technology (PIT) when developing their services or products, interacting with cross-sector stakeholders, obtaining funding, working with communities directly impacted by their solution, and holding values of social justice and equity would be invaluable for others working in the same field.
            </p>

            <p><strong>Problem Solving:</strong> Based on interview data the team previously obtained through conversation with 9 PIT BIPOC entrepreneurs, I identified one interesting knowledge-sharing challenge that technology could potentially support using the idea of journaling. I conducted an in-team experiment to test the idea and demonstrated the potential of sharing work journal entries to uncover knowledge-sharing opportunities. I collaborated with designers to refine the application concept and developed the first prototype as a web application. I also co-led or assisted with two grant applications to obtain funding, facilitate domain name brainstorming, and provide feedback to log design to support the deployment and branding effort.
            </p>

            <ul className="steps">
                <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }} >
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Analyze Interview Transcripts</p>
                        <p>9 PIT entrepreneurs</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">In-team experiment</p>
                        <p>4 team members</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Prototype</p>
                        <p>a team of two designers, and a developer/data scientist</p>
                    </div>
                </li>
                <li className="steps-segment  is-active" style={{ marginTop: "4px", listStyle: "none" }}>
                    <span className="steps-marker"></span>
                </li>
            </ul>
            <br />

            <div>
                <p><strong>Outcomes:</strong> I developed a web application using the Next.js framework that supports basic account login, sharing stories, and forming connections. The Google Cloud Platform credits grant application has been approved, which will allow hosting the platform for a year. I am currently in transition to a product manager role to organize a team of developers and designers to continue the development of this knowledge network platofrm for Black, Indigenous, and People of Color (BIPOC) entrepreneurs and prepare for deployment and beta testing.
                </p>

                <p><strong>Summary</strong>:</p>
                <nav className="level">
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Needs Finding 2nd analyssis</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">9 stakeholders</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Interactive System</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">1 web app</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Grant</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">2 grant applications</p>
                        </div>
                    </div>
                </nav>
            </div>
        </Fragment>
    },
    {
        title: `Estimating Programmer' Expertise to Augment Online Community Interaction`,
        // "Discount Expertise Metrics for Augmenting Community Interaction"
        description: `Expertise identification is important for various kinds of online and offline organizations, with practical applications such as supporting question answering, problem-solving, and team formation. Using developers as the target population, we demonstrate that it is possible to identify novices and experts of programming by examining the types of programming related websites they visit.`,
        status: `published`,
        details_link: `/discount-expertise/`,
        project_tag: `linkr`,
        image_info: {
            link: "/images/project/shahadat-shemul-gnyA8vd3Otc-unsplash_390x200.jpg", //  "/images/project/programmer_hackathon_hackny.jpg",
            original_link: ``, // `hackNY.org at Flickr`,
            license_info: {
                link: "https://unsplash.com/photos/gnyA8vd3Otc",
                // <div>Photo by <a href="https://unsplash.com/@shemul" target="_blank" rel="noopener noreferrer">Shahadat Shemul</a> on Unsplash</div>
                text: `Photo by Shahadat Shemul on Unsplash`
            }
        },
        role_list: ["Researcher", "Developer"],
        collaborator_list: ["Dr. Mark S. Ackerman"],
        method_list: ["Application Log Analysis", "Semi-structured Interview", "Prototyping"],
        skill_list: ["Python", "R", "ActionScript", "Adobe Flex", "scikit-learn"],
        topic_list: ["online community", "expertise estimation", "classification", "programmer"],

        // In knowledge-based (e.g., Q&A) online communities such as StackOverflow or Quora, knowing the expertise of a community member can greatly enhance interaction among community members: community members can better assess whom to ask questions, and estimate the level of details that will be appropriate for users who raise the questions. Obtaining an estimate, however, is difficult as people need to spend significant effort contributing (e.g., asking and answering questions) according to different rating mechanisms enforced by different communities to obtain an approximate indication of expertise.introduction: <Fragment>
        introduction: <Fragment>
            <p><strong>Background:</strong> This project is my first data-oriented research project. The work allowed me to practice basic approaches used in Natural Language Proocessing and Data Mining to analyze programmers' information-seeking behaviors. It also helped me develop a better understanding of how programmers at different levels approached online resources, which was beneficial for me as a programming instructor.</p>
            <p>The project got into motion after I completed my course work on information-seeking behavior and was learning data mining techniques through a course. I started discussing with my advisor on the relationship between a person's expertise in a domain and how the person went about searching for information online in the process of problem-solving. I picked programmers as the target of the investigation, as I have some intuition myself as a programmer that could benefit the exploration. I then designed and executed the study to investigate whether beginners and experts differed in terms of the types of online programming resources they visited.</p>
        </Fragment>,

        summary: <Fragment>
            <p><strong>Motivation:</strong> Having an estimate (e.g., in the form of classification such as level 1 to level 5) of expertise of a member within a knowledge-based online community (e.g., StackOverflow or Quora) is useful. For instance, community members can better assess to whom they should ask questions. Obtaining an estimate, however, is difficult as a significant contribution (e.g., asking and answering many questions) is usually required to generate an approximate indication of expertise.</p>

            <p><strong>Problem Solving:</strong> I hypothesized that a person's browsing history contained signals (i.e., page type such as API document or blog) for one's expertise in a subject area (e.g., programming). I analyzed the data collected from programmers (N=26) at different levels of expertise to verify this hypothesis. I applied support vector machine (SVM) to classify browsing records into different page types and used logic regression to model the strength of signals provided by different page types.</p>

            <br />
            <ul className="steps">
                <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }} >
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Interview & Data Collection</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Crawling Web Pages</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Classification</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Regression</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Data Analysis</p>
                    </div>
                </li>
                <li className="steps-segment is-active" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                </li>
            </ul>
            <figcaption style={{ textAlign: "center" }}>
            </figcaption>
            <br />
            <div>
                <p><strong>Outcomes:</strong></p>
                <ul>
                    <li><strong>Detecting novices and experts are plausible</strong>: page types can distinguish novices and experts to a certain degree.</li>
                    <li><strong>Detecting intermediates is still challenging</strong>: further analysis is required to provide estimate on programming expertise for people who are in between (neither a novice nor an expert), namely the intermediates.</li>
                </ul>
                <p>
                    The outcomes of this project include several scholarly publications (<a href="#publications">link</a>), a browser history software for users to review and filter their browsing history from Google Chrome and Firefox, and a system of components that access web resources and categorize them to estimate programmers' expertise.</p>

                <p><strong>Summary</strong>:</p>
                <nav className="level">
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Interview</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">26 programmers</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Data Collection & Analysis</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">201,700 URLs</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Scholarly Publications</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">3 articles</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Presentations</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">2 sessions</p>
                        </div>
                    </div>
                </nav>
            </div>
        </Fragment>
    },
    {
        title: `Enjoyable Training to Improve Visual-Motor Integration for Children with Autism`,
        description: `Some children with autism lack visual-motor integration ability. Traditional treatment activities are often time-consuming and thus prevent extensive training. We designed a computer game, called CuttingGame, to create a playful experience that both assesses and trains children in visual-motor integration to assist occupational therapists and the children.`,
        status: `published`,
        details_link: `/enjoyable-training-hand/`,
        project_tag: `cuttinggame`,
        image_info: {
            link: "/images/project/CuttingGame_390x200.jpg",
            original_link: ``,
            caption: ``,
            license_info: {
                link: ``,
                text: ``
            }
        },
        role_list: ["Project Leader", "Researcher", "Designer", "Developer"],
        collaborator_list: ["Dr. Jin-Ling Lo", " Dr. Hsin-Yen Wang", "Dr. Hao-Hua Chu", "Ya-Lin Hsieh"],
        method_list: ["Field Observation", "Co-design", "Prototyping", "Field Testing", "Application Log Analysis"],
        skill_list: ["ActionScript", "Adobe Flash", "C#", "Illustrator"],
        topic_list: ["health", "autism", "fine-motor skill", "occupational therapist", "game", "children"],
        // These developmental challenges can be tackled through early detection and extensive training. 
        introduction: <Fragment>
            <p><strong>Background:</strong> This is my master thesis project, and also my first research project in the area of Human-Computer Interaction in which I completed the process from initial observation, co-design with domain experts, prototyping, to evaluation in the form of one-month field deployment with real target users. This project inspired me to pursue research in the area of Human-Computer Interaction as I saw first-hand how the system I design and develop can make a meaningful and joyful impact on people, even just 7 children and their families.</p>
            <p>The research lab I was associated with when I was pursuing my master degree in Computer Science had a series of successful collaborations with another research lab at the Department of Occupational Therapy within the same university. Through a series of brainstorming sessions, we identified an opportunity to transform a therapy activity into the computer game. I then led the design, development, and evaluation of a computer game, in collaboration with occupational therapists, under the mentorship of two faculty, one from each department.</p>
        </Fragment>,
        summary: <Fragment>
            <p><strong>Motivation:</strong> Children with autism are likely to show difficulty with fine motor skills and visual-motor integration. The conventional solution is for children to attend training sessions led by occupational therapists. Unfortunately, such sessions usually happen at most weekly, which is too infrequent for them to gain the maximum benefit.</p>

            <p><strong>Problem Solving:</strong> Drawing from my 5-month field observations of training sessions, I developed a computer paper-cutting game that mimicked a training activity. I worked with 7 children with autism and their families to conduct a one-month field deployment. I also developed additional tools for occupational therapists to review children's gameplay and provide more detailed feedback.</p>

            <br />
            <ul className="steps">
                <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Field Observation</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Co-design</p>
                        <p>2 occupational therapists</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Development</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Field Testing</p>
                        <p>7 families for a month</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Data Analysis</p>
                    </div>
                </li>
                <li className="steps-segment is-active" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                </li>
            </ul>
            <figcaption style={{ textAlign: "center" }}>
            </figcaption>
            <br />

            <div>
                <p><strong>Outcomes:</strong></p>
                <ul>
                    <li><strong>Ability improved</strong>: children's visual-motor integration capability, approximated by in-game performance,   had improved.</li>
                    <li><strong>Children enjoyed it</strong>: children enjoyed playing the 'game' daily, which enabled intensive training.</li>
                </ul>
                <p>
                    The outcomes of this project include CuttingGame, a desktop training game for children, a game manual, a set of tools for occupational therapists to review children's progress, and two scholarly publications (<a href="#publications">link</a>).
                </p>

                <p><strong>Summary</strong>:</p>
                <nav className="level">
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Field Observation</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">5 months</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Co-design</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">2 therapists</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Interactive Systems</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">2 apps</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Fieled Testing</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">7 children & family</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Scholarly Publications</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">2 articles</p>
                        </div>
                    </div>
                </nav>
            </div>
        </Fragment>
    },
    {
        title: `TraceViz: Finding Location Data through Brushing to Support Application Testing`,
        description: `Location data (e.g., GPS) enable a plethora of useful location-based application (e.g., Yelp). These application use location data to infer meaningful users' characteristics and behaviors, such as interest, favorite places, and jogging route. To create such applications, developers need a way to effectively test how applications react to different location patterns. We create TraceViz, a tool that provides a set of filters for finding GPS traces, and a natural 'brushing' interaction for selecting traces. Preliminary evaluation shows that users can use TraceViz to efficiently find location traces needed for testing a sample location-based application.`,
        status: `published`,
        details_link: `/traceviz-location-data-brush/`,
        project_tag: `replay`,
        image_info: {
            link: "/images/project/TraceViz_390x200.jpg",
            original_link: ``,
            caption: ``,
            license_info: {
                link: ``,
                text: ``
            }
        },
        role_list: ["Researcher", "Designer", "Developer"],
        collaborator_list: ["Dr. Yung-Ju Chang", "Dr. Mark W. Newman"],
        method_list: ["Semi-structured Interview", "Prototyping", "Usability Testing"],
        skill_list: ["ActionScript", "Adobe Flex", "Android"],
        topic_list: ["mobile app", "visualization", "tool", "developer"],
        introduction: <Fragment>
            <p><strong>Background:</strong> This project was the result of volunteer and course work when I was pursuing my Master's degree at the University of Michigan. I collaborated with a Ph.D. student, Yung-Ju Chang, to design TraceViz for developers of location-based applications. I led the implementation of different filters for developers to refine the search criteria of GPS traces. I also developed a location-based Android app for study participants (e.g., developers) to modify in the evaluation study of TraceViz.</p>

            <p>This project was unique for me as we were developing a tool to support developers to create applications. In the design process, I needed to consider not only what developers needed, but also what applications developers might create for app users so that our design could provide proper support.</p>

        </Fragment>,
        summary: <Fragment>
            <p><strong>Motivation:</strong> Location data (e.g., GPS) enable location-based applications (e.g., Yelp) to infer meaningful users' characteristics and behaviors (e.g., interests, favorite places, and jogging routes). Application developers need a way to efficiently find data to test how applications react to different location patterns effectively.</p>

            <p><strong>Problem Solving:</strong> TraceViz was created to provide a set of filters for finding GPS traces, and a natural 'brushing' interaction for selecting traces. Users could select location traces that pass through a segment of a path (e.g., walking on State street from a coffee shop to a dormitory) by painting the segment using the map interface.</p>

            <br />
            <ul className="steps">
                <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }} >
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Brainstorm Design</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }} >
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Development</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }} >
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Pilot Testing</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }} >
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Revision</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }} >
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Usability Testing</p>
                    </div>
                </li>
                <li className="steps-segment is-active" style={{ listStyle: "none" }} >
                    <span className="steps-marker"></span>
                </li>
            </ul>
            <figcaption style={{ textAlign: "center" }} >
            </figcaption>
            <br />

            <div>
                <p><strong>Outcomes:</strong> The lab-based user study (N=8) showed that </p>
                <ul>
                    <li><strong>TraceViz provides efficient tools to filter data</strong>: participants could use TraceViz to efficiently find location traces needed for testing a sample location-based application.</li>
                    <li><strong>The brushing interaction is easy to apply</strong>: participants were able to flexibly apply the brushing interaction to narrow down desirable location traces.</li>
                </ul>
                <p>
                    The outcomes of this project include multiple scholarly publications (<a href="#publications">link</a>), an Android application that facilitates social sharing of location data to support group activity (e.g., meeting) as the target of testing, and the TraceViz software that app developers can use to test their location-based apps.</p>

                <p><strong>Summary</strong>:</p>
                <nav className="level">
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Interactive System</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">1 desktop app & 1 Android app</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Usability Testing</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">8 participants</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Scholarly Publications</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">2 articles</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Presentation</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">1 session</p>
                        </div>
                    </div>
                </nav>
            </div>
        </Fragment>
    },
    {
        title: `Translating Viewpoints on Health Management into Actionable Practices`,
        // Translating Viewpoints on Health Management into Actionable Practices
        // Information Translation for Viewpoints on Health Management
        description: `Managing health with a chronic condition often involves interaction with family members, neighbors, community health workers, and health professionals, who contribute different viewpoints to help the individuals navigate through all the challenges. We design a video-based mobile platform for people to easily present and understand various viewpoints for managing chronic conditions such as Diabetes.`,
        status: `published`,
        details_link: `/health-viewpoints/`,
        project_tag: `fit`,
        image_info: {
            link: `/images/project/fit_illustration_390x200.jpg`, // `../images/project/community_health_conversation_publicdomainfiles.jpg`,
            original_link: ``, //`http://www.publicdomainfiles.com/show_file.php?id=13393039217622`,
            caption: ``, //`Photo by Cristina Gottardi on Unsplash`,
            license_info: {
                link: ``,
                text: ``
            }
        },
        role_list: ["Researcher", "Designer", "Developer"],
        collaborator_list: ["Dr. Liz Kaziunas", "Shuang Liu", "Dr. Mark S. Ackerman"],
        method_list: ["Prototyping", "Usability Testing", "Semi-structured Interview"],
        skill_list: ["Javascript", "CSS", "HTML", "Angular", "Illustrator"],
        topic_list: ["health", "care", "chronic condition", "diabetes", "patient"],
        // Dealing with a chronic condition such as diabetes is not an easy task. 
        // Following such instructions is difficult because instructions such as 
        // The community might be located at a place with safety issues, so jogging 30 minutes each day might not be a good idea.
        // One of the challenges of dealing with a chronic condition such as diabetes is to follow 
        // In other words, featuring multiple perspectives is beneficial, but further customization is needed to accommodate people's experience.
        introduction: <Fragment><p><strong>Background:</strong> I joined this project after the phase of understanding users was almost completed. I was tasked to design and implement prototypes to tackle the user challenges identified by a senior PhD student, Liz Kaziunas. Under the mentorship of Dr. Mark Ackerman, I went through several iterations of design and development, including low-fi paper sketches, an Android app as the proof of concept, and a web application as the final product. I led a preliminary study to gather feedback about our design.</p>

            <p>This was an interesting and equally challenging project where I experienced vividly the culture differences between Taiwan and the U.S., specifically in terms of health management attitudes toward different approaches. While Chinese medicine and Western medicine were practiced and appreciated by many people in Taiwan, Western medicine was rather dominant for my U.S. participants. I have received some relatively strong opinions from U.S. participants, which showed that further exploration was needed.</p>
        </Fragment>,
        summary: <Fragment>
            <p><strong>Motivation:</strong> Following health professionals' instructions to manage a chronic condition (i.e, diabetes), such as "eating healthy food" and "doing more exercise" could be challenging. Patients might need resources to figure out how to realistically implement such instructions in their community with its unique environment. For instance, there might be no grocery store in the vicinity that provides healthy food options.</p>

            <p><strong>Problem Solving:</strong> Based on our prior study that recognized the need for translation to convert high-level instructions into realistic and localized strategies (e.g., finding a commute option to a grocery store that offers healthy food options), I designed and evaluted a video-based tool that showcases how other patients with a similar condition develop their viewpoints and approaches to manage chronic conditions.</p>

            <ul className="steps">
                <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }} >
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Prior Research Finings</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }} >
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Lo-Fi</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Hi-Fi</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Usability Testing</p>
                        <p>6 participants</p>
                    </div>
                </li>
                <li className="steps-segment is-active" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Re-design</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                </li>
            </ul>
            <figcaption style={{ textAlign: "center" }}>
            </figcaption>
            <br />

            <div>
                <p><strong>Outcomes:</strong></p>
                <p>Preliminary user study with 6 participants, who were patients or had a patient in the family, showed</p>
                <ul>
                    <li><strong>Seeing different viewpoints is valuable</strong>: participants valued seeing different viewpoints and approaches to managing diabetes.</li>
                    <li><strong>Facilitate understanding family's viewpoints</strong>: the application helped participants to better understand the viewpoints held by their family members and their approaches to managing diabetes.</li>
                    <li><strong>Prior experience might dominate</strong>: experience, particularly a negative one, could strongly influence how a participant perceives specific viewpoints (e.g., Chinese medicine).</li>
                </ul>
                <p>The outcomes of this project include a scholarly publication (<a href="#publications">link</a>) and a mobile web prototype that allows patients to explore curated Youtube videos where patients and clinicians with different viewpoints share their practices of managing diabetes.</p>

                <p><strong>Summary</strong>:</p>
                <nav className="level">
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Interactive System</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">1 web app</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Usability Testing</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">6 participants</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Scholarly Publication</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">1 article</p>
                        </div>
                    </div>
                </nav>
            </div>
        </Fragment>
    },
    {
        title: `NQRemix: Expressing Self Identity and Fostering Community in Student Dorms`,
        description: `NQRemix is an interactive magnetic poetry game for the dorm residents at the University of Michigan to express self identity and foster community.`,
        status: `course project`,
        details_link: `/nqremix-self-community/`,
        project_tag: `nqremix`,
        image_info: {
            link: "/images/project/NQRemix_390x200.jpg",
            original_link: ``,
            caption: ``,
            license_info: {
                link: ``,
                text: ``
            }
        },
        role_list: ["Project Milestone Leader", "Researcher", "Lead Developer"],
        collaborator_list: ["Sayan Bhattacharyya", "Danlu Fu", "Travis Lafleur", "Erin Zolkosky"],
        method_list: ["Contextual Inquiry", "Persona", "Scenario", "Storyboarding", "User Enactment", "Prototyping", "Thematic Analysis"],
        skill_list: ["ActionScript", "Adobe Flex"],
        topic_list: ["community interaction", "dormitory", "social media", "student"],
        introduction: <Fragment><p><strong>Background: </strong> This was a course project for Pervasive Interaction Design. The course focused on designing interactive applications through different computing devices (e.g., phones and kiosks) in our environment, as opposed to personal computers. As one of the two members on our team with a software development background, I led the milestone of high-fi prototyping.</p>

            <p>To facilitate collaboration, I designed a crash course of prototyping using Adobe Flex for my teammates so that designers, content writer, and developers on our team could collaborate using Adobe tools. I also served as the lead developer to design the overall system architecture and implement the majority of the components.</p></Fragment>,
        summary: <Fragment>
            <p><strong>Motivation:</strong> North Quadrangle (NQ) is an academic-dorm complex where students can learn and live together. NQ offers an excellent opportunity for college students to connect with fellow students and form identity. However, as an academic-dorm complex, doors are mostly closed, and busy students pass by without interaction.</p>

            <p><strong>Problem Solving:</strong> Our team performed a series of design exploration including interviews, persona, user enactment with residents, and hi-fi prototype for a live demonstration. The exploration confirmed our initial observation ("NQ is not social") and led us to design a kiosk that presented itself at areas where people would have a brief pause (e.g., in or in front of an elevator) in their busy lives when they could engage with other residents. The goal was to foster community interaction and develop each individual's unique identity.</p>

            <br />
            <ul className="steps">
                <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }} >
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Contextual Inquiry</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Persona & Scenario</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">User Enactment</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Prototype</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Public Demonstration</p>
                    </div>
                </li>
                <li className="steps-segment is-active" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                </li>
            </ul>
            <figcaption style={{ textAlign: "center" }}>
            </figcaption>
            <br />

            <div>
                <p><strong>Outcomes:</strong> The outcomes of this project is a kiosk application, NQRemix, that allows residents to see and remix location-filtered and tag-based social media feeds. Residents can mix and match in a magnetic poetry style to remix concepts and ideas that community members generate.</p>

                <p><strong>Summary</strong>:</p>
                <nav className="level">
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Interactive System</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">1 desktop app</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Demonstration</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">1 session</p>
                        </div>
                    </div>
                </nav>
            </div>
        </Fragment>
    },
    {
        title: `Aoide: A Genre and Timeline Aware Browsing for MLibrary Music Collections`,
        //   "Aoide: A Virtual Browsing System for MLibrary Music"
        description: `Aoide use visualization to create a new browsing interface for University Library’s music collection. The visualization utilizes genre and artist information to create a music exploration space for patrons.`,
        status: `independent project`,
        details_link: `/aoide-browsing-music/`,
        project_tag: `aoide`,
        image_info: {
            link: "/images/project/Aoide_390x200.jpg",
            original_link: ``,
            caption: ``,
            license_info: {
                link: ``,
                text: ``
            }
        },
        role_list: ["Project Coordinator", "Researcher", "Designer"],
        collaborator_list: ["Sylvia Lai", "Pei-Chih Shih", "Yi-Ying Lin", "Gin Chieng"],
        method_list: ["Contextual Inquiry", "Persona", "Scenario", "Usability Testing", , "Thematic Analysis"],
        skill_list: ["Illustrator"],
        topic_list: ["music", "library", "visualization", "browsing"],
        comment_list: ["Award: University of Michigan Library iDesign Competition Second Place"],
        introduction: <Fragment>
            <p><strong>Background: </strong>This was a self-initiated project that won my team second place in a design competition. I assembled a team of fellow students who wanted to hone their design and research skills to improve access to the music collection within the university library. I participated in conducting research and also take the lead to design the timeline-based album browsing interface in the final design.</p>

            <p>Aside from the submission and presentation deadlines set by the competition, I had to work with my teammates to make a plan and distribute the work among ourselves. We not only need to meet our internal deadlines but also accommodate scheduling issues with other course work and opportunity for presentation to showcase our work and get feedback. Finishing this project certainly gave me a better perspective on how to lead a self-initiated project from initiation to completion. I shared my experience with other masters students I mentored later, which led them to become the finalist of another design competition in 2016 (see Mentorship).</p>

        </Fragment>,
        summary: <Fragment>
            <p><strong>Motivation:</strong> Browsing a library's catalog is not an easy task as the standard search-results-in-a-list design often fails to show the intricate relationships between works by different authors and how an author's works evolve. Music is one such category where the standard interface does not support navigation of albums in different dimensions (e.g., genre, artist, and time).</p>

            <p><strong>Problem Solving:</strong> Through engaging library patrons and drawing influences from other catalogs, we developed Aoide, a browsing system that allowed patrons to navigate the music collection through visualization that highlight relationships between artists and genres, and presented the ways how artists' styles evolve over time.</p>

            <br />
            <ul className="steps">
                <li className="steps-segment" style={{ marinTop: "4px", listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Contextual Inquiry</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Personas & Scenarios</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Comparative Analysis</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Lo-Fi</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Usability Test</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Hi-Fi</p>
                    </div>
                </li>
                <li className="steps-segment  is-active" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                </li>
            </ul>

            <div>
                <p><strong>Outcomes:</strong> The outcomes of this project include a website documenting the research process and findings, an interactive prototype, hi-fi illustrations, and an award from the Unversity of Michigan Library iDesign competition.</p>

                <p><strong>Summary</strong>:</p>
                <nav className="level">
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Interviews</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">7</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Comparative Analysis</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">15 sites</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Interactive Prototype</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">1 web app</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Usability Testing</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">6</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Presentations</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">2</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Award</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">2nd</p>
                        </div>
                    </div>
                </nav>
                <br />
            </div>
        </Fragment>
    },
    {
        title: `Evaluation and Recommendations for University Library’s Mobile Catalog`,
        description: `We apply user experience research techniques to evaluate Mirlyn Mobile, the mobile web interface for University of Michigan Library Mirlyn Catalog.`,
        status: `course project`,
        details_link: `/mirlyn-mobile-evaluation/`,
        project_tag: `mirlyn-mobile`,
        image_info: {
            link: "/images/project/MirlynMobile_390x200.jpg",
            original_link: ``,
            caption: ``,
            license_info: {
                link: ``,
                text: ``
            }
        },
        role_list: ["Client Liaison", "Researcher"],
        collaborator_list: ["Justin Feezell", "Yi-Ying Lin", "Karen Stover"],
        method_list: ["Contextual Inquiry", "Persona", "Scenario", "Interaction Map", "Heuristic Evaluation", "Comparative Analysis", "Survey", "Usability Testing"],
        skill_list: ["Qualtrics", "Omnigraffle"],
        topic_list: ["library", "browsing", "mobile UI"],
        introduction: <Fragment>
            <p><strong>Background: </strong>This was a course project for Evaluation of Systems and Services. The course focused on learning and practicing a series of research methods to evaluate interactive systems and services. The goal was to provide recommendations for improvement. I was put on a 4-person team and got matched with the University Library to evaluate their newly developed mobile catalog, Mirlyn Mobile.</p>
            <p>To learn and practice different methods in a semester, I normally had 2+ weeks to execute the method and summarize the results. The schedule forced me to think more critically about what was feasible to do and what the most important questions to ask were. Executing this project also provided me an opportunity to see how various methods (e.g., interviews, heuristic evaluation, and usability testing) differed and complemented each other for identifying issues and probable causes.</p>
        </Fragment>,
        summary: <Fragment>
            <p><strong>Motivation:</strong> With the popularization of online resources such as Wikipedia and digitization of publication (e.g., ebook and mp3) of multimedia, a library needs to make itself accessible and convenient so that people can better utilize the abundance of resources the library provides.</p>

            <p><strong>Problem Solving:</strong> Through interviews, survey, heuristic evaluation, and usability testing, we provided recommendations to improve the library's mobile catalog, Mirlyn Mobile.</p>

            <br />
            <ul className="steps">
                <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Contextual Inquiry</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Persona & Scenarioos</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Heuristics Evaluation & Comparative Analysis</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Survey</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Usability Testing</p>
                    </div>
                </li>
                <li className="steps-segment is-active" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                </li>
            </ul>
            <figcaption style={{ textAlign: "center" }}>
            </figcaption>
            <br />

            <div>
                <p><strong>Outcomes:</strong> The outcomes of this project include a research report that documents our process, findings, and recommendations, and the changes implemented on the mobile catalog of the University of Michigan's library.</p>

                <p><strong>Summary</strong>:</p>
                <nav className="level">
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Presentation</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">1</p>
                        </div>
                    </div>
                </nav>
            </div>
        </Fragment>
    },
    {
        title: `Emotioner: A Mobile App to Help Students Share and Understand Emotion Better`,
        description: `Emotioner is a Android mobile application that manages personal emotion. The goal is to help users reflect on their emotions and be aware of others’ emotions.`,
        status: `independent project`,
        details_link: `/emotioner-emotion-community/`,
        project_tag: `emotioner`,
        image_info: {
            link: "/images/project/emotioner_illustratioon_390x200.jpg",
            original_link: ``,
            caption: ``,
            license_info: {
                link: ``,
                text: ``
            }
        },
        role_list: ["Project Co-leader", "Designer"],
        collaborator_list: ["Ying-Yu Chen"],
        method_list: ["Prototyping"],
        skill_list: ["Axure", "iMovie"],
        topic_list: ["emotion", "community", "mental health", "mobile UI", "student"],
        comment_list: ["Award: University of Michigan Mobile Apps Challenge Runner-Up"],
        introduction: <Fragment>
            <p><strong>Background: </strong>This was a self-initiated project for the University of Michigan Mobile App Challenge. I collaborated with a fellow master student, Ying-Yu Chen to brainstorm and refine the idea, and occasionally getting feedback from a faculty specialized in interaction design. In addition to refining ideas, I participated in prototyping using Axure. I also wrote the script, narrated, and recorded the demonstration video as the challenge submission entry.</p>

            <p>This is possibly the shortest design project I have engaged with. It was rather challenging to work on such important topic, from ideation to submission, within two weeks. While I believe the idea and the prototype could be improved much further, it also allowed me to crystallize the main concept I wanted to deliver, in a much focused and simple way. More importantly, the design process also helped me to reflect on my own approaches to mental health in grad school.</p>
        </Fragment>,
        summary: <Fragment>
            <p><strong>Motivation:</strong> Attending college often mean going to a new environment, where everything needs to be figured out again. In this new environment, students meet new people and manage challenging school work. Dealing with all these unknowns, students might experience mood swing and other mental health issues.</p>

            <p><strong>Problem Solving:</strong> I developed the concept of Emotioner, a smartphone app for college students to manage their emotion through recording and sharing. Students could anonymously share their emotion and see 'how the campus is feeling' by looking at a campus map overlayed with emotion (e.g., emoticon) generate by residents of different buildings and schools. The goal was to help users reflect on their emotions and be aware of others' emotions so that they can better manage their college life.</p>

            <br />
            <ul className="steps">
                <li className="steps-segment" style={{ marginToop: "4px", listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Brainstorm Design</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Expert Feedback</p>
                        <p>A college professor</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Prototype</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Create Demonstration Video</p>
                    </div>
                </li>
                <li className="steps-segment is-active" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                </li>
            </ul>
            <figcaption style={{ textAlign: "center" }}>
            </figcaption>
            <br />
            <div>
                <p><strong>Outcomes:</strong> The outcomes of this project include an interactive prototype, a conceptual video explaining the concept of Emotioner, and a competition award from the University of Micigan Mobile Apps Center.</p>

                <p><strong>Summary</strong>:</p>
                <nav className="level">
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Expert Feedback</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">1 college professor</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Interactive Prototype</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">1 app</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Award</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">runner-up</p>
                        </div>
                    </div>
                </nav>
            </div>
        </Fragment>
    },
    {
        title: `Installation Games for Information Technology and Health Issues Education`,
        description: `Dance Dance Revolution and Fight Bacteria are two installation games that are used to educate children and adolescents about information technology and health issues in for National Science Council.`,
        status: `freelance project`,
        details_link: `/ddr-fight-bacteria/`,
        project_tag: `ddr`,
        image_info: {
            link: "/images/project/ScienceExhibition_390x200.jpg",
            original_link: ``,
            caption: ``,
            license_info: {
                link: ``,
                text: ``
            }
        },
        role_list: ["Project Coordinator", "Developer"],
        collaborator_list: ["Jen-Hao Chen", "Ho-Lin Chang"],
        method_list: ["Prototyping"],
        skill_list: ["ActionScript", "Adobe Flash"],
        topic_list: ["health", "information technoology", "learning", "game", "general public"],
        introduction: <Fragment><p><strong>Background: </strong>This was an opportunity referred by a designer I met in a class about Internet arts. The studio that was in charge of two installation games as part of a science exhibition needed a developer team, so I gather two of my classmates studying computer science to take on the opportunity. In addition, to work with my teammates to design and implement the mechanisms behind the two games, I served as the portal for the designer team and the hardware team, so that the three teams could coordinate to finish the project on time for a science exhibition.</p>

            <p>I learned two important lessons on application design and project management from this project. First, for an installation game designed to engage with the general public, there was a delicate balance between the expected and the unexpected. We wanted the unexpected in games to create surprise, and yet provide some recognizable patterns for people to make sense of it, espeically for installation games with a large audience in a science exhibition. Second, to coordinate a multi-team project, I needed to talk to different teams to understand their job content and working rhythms, so that I can empathize with them to accommodate any contingency that could delay their progress, and hence ours. Moreover, by anticipating potential roadblocks, I could revise the schedule for our team to properly making progress.</p>

        </Fragment>,
        summary: <Fragment>
            <p><strong>Motivation:</strong> Engaging the public, especially youths, with history is not always an easy task, as people might get bored quickly if not particularly interested in the subjects. One way to tackle the issue is to engage the audience with a fun activity while history is delivered.</p>

            <p><strong>Problem Solving:</strong> Working with visual designers and an interactive installation artist, I led a software development team of 3 to create two installation games in Taiwan for the National Science Council's annual exhibition, Taiwan's Amazing Transformation in Science and Technology.</p>

            <br />
            <ul className="steps">
                <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Understanding Design Requirements</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Development</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Expert Feedback</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Revision</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Release</p>
                    </div>
                </li>
                <li className="steps-segment is-active" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                </li>
            </ul>
            <figcaption style={{ textAlign: "center" }}>
            </figcaption>
            <br />


            <div>
                <p><strong>Outcomes:</strong> A Dance Dance Revolution game was created for the audience to access important episodes of information technology development in the history of Taiwan. People can use their feet or hands to reproduce the suggested sequence of steps to unlock a new episode. A Whac-A-Mole game was created for the audience to engage with health education materials. People can 'hit' different bacteria and get to know how vaccines were introduced at different times to help people maintain their health.</p>

                <p><strong>Summary</strong>:</p>
                <nav className="level">

                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Interactive Systems</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">2 installation games</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Expert Feedback</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">An exhibition manager</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Exhibition</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">3 months</p>
                        </div>
                    </div>
                </nav>
            </div>
        </Fragment>
    },
    {
        title: `A Persuasive Poster to Encourage Children to Organize their Rooms`,
        description: `Smart Poster is a digital poster that persuades young children to pick up stuffs themselves. The poster shows a virtual world, which can be affected by the child’s tidiness behavior in the physical world.`,
        status: `course project`,
        details_link: `/smart-poster-persuasive-children/`,
        project_tag: `smartposter`,
        image_info: {
            link: "/images/project/SmartPoster_390x200.jpg",
            original_link: ``,
            caption: ``,
            license_info: {
                link: ``,
                text: ``
            }
        },
        role_list: ["Designer", "Developer"],
        collaborator_list: [],
        method_list: ["Prototyping"],
        skill_list: ["ActionScript", "Adobe Flash", "C#", "RFID", "Illustrator"],
        topic_list: ["sensor", "RFID", "persuasive technology", "children"],
        // The idea is to give children a display that could potentially persuade them to be tidy.
        introduction: <Fragment><p><strong>Background: </strong>This was a course project for Ubiquitous Computing. I developed and implemented the idea to use RFID to detect the presence of objects so that I could design a virtual world poster that parents can use to persuade young children to keep things organized.</p>
            <p>
                This was the first time I created an application using sensors other than those used in consumer devices (e.g., accelerometers or GPS). Working with the RFID sensors and the antennas directly allowed me to understand the constraints and devised a solution for my project.</p>
        </Fragment>,
        summary: <Fragment>
            <p><strong>Motivation:</strong> Facilitating the development of a good habit is difficult. Parents might need to spend a lot of time to educate their children to develop a habit such as keep things organized.</p>

            <p><strong>Problem Solving:</strong> I created Smart Poster, a digital poster that persuades young children to organize their stuff. The poster shows a virtual world, which can be affected by the tidiness behavior of a child in the physical world. The system uses RFID sensors to detect whether children' belongings, with RFID tags, are organized in the appropriate places. If everything is in place, the poster shows a sunny day and playful animals. Otherwise, the weather turns bad, and the animals get sick.</p>

            <br />
            <ul className="steps">
                <li className="steps-segment" style={{ marginTop: "4px", listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Brainstorm Design</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Prototype</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Iterative Design</p>
                    </div>
                </li>
                <li className="steps-segment" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                    <div className="steps-content is-divider-content">
                        <p className="is-size-5">Demonstration Video</p>
                    </div>
                </li>
                <li className="steps-segment is-active" style={{ listStyle: "none" }}>
                    <span className="steps-marker"></span>
                </li>
            </ul>
            <figcaption style={{ textAlign: "center" }}>
            </figcaption>
            <br />

            <div>
                <p><strong>Outcomes:</strong> The outcomes include a demonstration video and an interactive prototype that displays a virtual world where animals and plants' behaviors will gradually change depending on whether items (e.g., toys) with RFID tags have been placed in several sensor-augmented boxes.</p>


                <p><strong>Summary</strong>:</p>
                <nav className="level">
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Interactive System</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">1 desktop app</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Demonstration</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">1 concept video</p>
                        </div>
                    </div>
                </nav>
            </div>
        </Fragment>
    },
    {
        title: `Active Slides: Facilitating Instructor-Student In-Class Communication`,
        description: `Active Slides is a platform that encouraged more communication and made the education process more interesting in class.`,
        status: `independent project`,
        details_link: `/active-slides-classroom-communication/`,
        project_tag: `activeslides`,
        image_info: {
            link: "/images/project/ActiveSlides_390x200.jpg",
            original_link: ``,
            caption: ``,
            license_info: {
                link: ``,
                text: ``
            }
        },
        role_list: ["Developer"],
        collaborator_list: ["Jen-Hao Chen", "Li-Shan Wang"],
        method_list: ["Prototyping", "Field Testing"],
        skill_list: ["C#"],
        topic_list: ["classroom", "learning", "communication", "instructor", "student"],
        introduction: <Fragment><p><strong>Background: </strong>This was the project for an independent study in my senior year. It was my first project with a Human-Computer Interaction (HCI) flavor, which jumpstarted my interest in HCI and user experience. I worked with two classmates of mine to design a presentation system to facilitate instructor-student communication in class.</p>
            <p>I implemented the student-facing web interface and also the server-side service that channel students' feedback to the instructor interface. I got to deploy the system to an undergraduate Python programming course for one lecture and observed how the interaction between the students and the instructor change with the new system.</p></Fragment>,
        summary: <Fragment>
            <p><strong>Motivation:</strong> Students are sometimes shy in expressing their difficulty in understanding course content. This leaves the teacher unaware of the problem and creates a gap of understanding between teachers and students in a class.</p>

            <p><strong>Problem Solving:</strong> I led a team to build “Active Slides,” a platform that encouraged more communication and made the education process more interesting as it happened in class. By allowing students to add some visual effects to the slides, students were able to provide feedback collaboratively as a group for the instructor, and also created a shared understanding of how learning materials (i.e., slides) were perceived by fellow learners.</p>

            <br />
            <ul class="steps">
                <li class="steps-segment" style={{ marginTop: "4px", listStyle: "none" }}>
                    <span class="steps-marker"></span>
                    <div class="steps-content is-divider-content">
                        <p class="is-size-5">Brainstorming Features</p>
                    </div>
                </li>
                <li class="steps-segment" style={{ listStyle: "none" }}>
                    <span class="steps-marker"></span>
                    <div class="steps-content is-divider-content">
                        <p class="is-size-5">Development</p>
                    </div>
                </li>
                <li class="steps-segment" style={{ listStyle: "none" }}>
                    <span class="steps-marker"></span>
                    <div class="steps-content is-divider-content">
                        <p class="is-size-5">Expert Feedback</p>
                        <p>2 college professors</p>
                    </div>
                </li>
                <li class="steps-segment" style={{ listStyle: "none" }}>
                    <span class="steps-marker"></span>
                    <div class="steps-content is-divider-content">
                        <p class="is-size-5">Revision</p>
                    </div>
                </li>
                <li class="steps-segment" style={{ listStyle: "none" }}>
                    <span class="steps-marker"></span>
                    <div class="steps-content is-divider-content">
                        <p class="is-size-5">Fieled Testing</p>
                        <p>A Python programming class</p>
                    </div>
                </li>
                <li class="steps-segment is-active" style={{ listStyle: "none" }}>
                    <span class="steps-marker"></span>
                </li>
            </ul>
            <figcaption style={{ textAlign: "center" }}>
            </figcaption>
            <br />

            <div>
                <p><strong>Outcomes:</strong> Initial results of the field testing showed that</p>
                <ul>
                    <li><strong>The application stimulates short pauses for Q&A</strong>: students were able to provide implicit feedback that caught the attention of the instructor, which resulted in pauses of the lecture and creating time for Q&A.</li>
                    <li><strong>Students enjoy it, maybe a bit too much.</strong>: students enjoyed using the application, and even start having fun with the application's painting capability to draw interesting figures.</li>
                </ul>

                <p>
                    The outcomes of this project include a web-based client that allows students to view slides and submit feedback, and a desktop software for instructors to import and present their slides and receive students feedback in real-time.</p>

                <p><strong>Summary</strong>:</p>
                <nav className="level">
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Interactive System</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">1 desktop app and 1 web app</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Expert Feedback</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">2 college professors</p>
                        </div>
                    </div>
                    <div className="level-item has-text-centered">
                        <div>
                            <p className="heading">Filed Testing</p>
                            <p className="is-size-4 is-size-5-mobile has-text-weight-semibold">1 class</p>
                        </div>
                    </div>
                </nav>
            </div>
        </Fragment>
    }
];